/* eslint-disable max-len */
import { Injectable, OnDestroy, inject } from '@angular/core';

import { Observable, of, BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import moment from 'moment-timezone';
import { intersection } from 'lodash';

import { FilterService } from '../../../Common/Filter/services/abstract-filter.service';
import { PaginatorServiceInterface } from '../../../Common/Misc/services/paginator.service.interface';
import { PageCriteriaModelInterface } from '../../../Common/Misc/models/page-criteria.model.interface';
import { TargetingSearch } from '../models/targeting-search.model';
import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { TargetingResultModel } from '../models/targeting-result.model';
import { TargetingList } from '../models/targeting-list.model';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { FilterTypeTextModel } from '../../../Common/Filter/models/filter-type-text.model';
import { TargetingFiltersEnum } from '../../../Common/TargetingShared/enum/targeting-filters.enum';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { SortOrderServiceInterface } from '../../../Common/Misc/services/sort-order.service.interface';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { TargetingSearchFacetsEnum } from '../../../api/targeting/enum/targeting-search-facets.enum';
import { FacetResult, TargetingSearchResponseApiModel } from '../../../api/targeting/model/targeting-search-response.api.model';
import { JournodexSubjectsService } from '../../../Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../Core/services/journodex-enums.service';
import { JobRoleModel } from '../../Core/models/job-role.model';
import { EntityIdApiModel } from '../../../api/targeting/model/entity-id-api-model';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { ListMembershipApiModel } from '../../../api/targeting/model/list-membership-api-model';
import { StatusFilterData, StatusFilterModel } from '../components/TargetingResults/StatusFilter/status-filter.model';
import { TargetingListList } from '../models/targeting-list-list.model';
import { RectMapQuery } from '../../../Discover/Targeting/models/targeting-search.model';
import { ReachFilterModel } from '../../../Common/TargetingShared/model/reach-filter.model';
import { LanguageModel } from '../../../Core/Localization/models/language.model';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { FilterGroupModel } from '../../../Common/Filter/models/filter-group.model';
import { BadgeFilterTypesEnum } from '../models/badge-filter.model';
import { FilterTypeStreamContentCheckboxModel } from '../components/TargetingResults/StreamContentCheckboxFilter/stream-content-checkbox.model';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { IdNamePairModel } from '../../../Common/Misc/models/id-name-pair.model';
import { TargetingResultFactory } from '../factories/targeting-result.factory';
import { promiseOnlyResolved } from '../../../Core/utils';
import { circulationTypeToSortType } from '../models/circulation.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { timezoneConversionDateFormat } from '../../../Common/Forms/helpers/time.helper';
import { TargetingFilterFactory } from '../factories/targeting-filter.factorty';
import { IncludeExcludeEnum } from '../../../Common/Filter/enums/include-exclude.enum';
import { DistributionSummaryModel } from '@Common/TargetingShared/model/distribution-summary-model';
import { DistributionApi } from '@api/distributions/distribution.api';
import { TargetingMediaTypeEnum } from '../enums/targeting-media-type.enum';

export class FacetValueModel {
    count: number;
    value: string;
}
export interface TargetingFacetCollection {
    // dateFacets: Map<DateFacetTypesEnum, DateFacetValueModel[]>;
    facets: Map<TargetingSearchFacetsEnum, FacetValueModel[]>;
}

@Injectable()
export abstract class BaseSearchService extends FilterService implements OnDestroy, PaginatorServiceInterface, SortOrderServiceInterface {

    protected subscriptions: Subscription[] = [];

    protected searchLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    protected filtersLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    protected pageCriteria: PageCriteriaModelInterface = {page: 1, pageSize: 25};
    protected pageCriteria$: BehaviorSubject<PageCriteriaModelInterface> = new BehaviorSubject<PageCriteriaModelInterface>(this.pageCriteria);

    protected nResults: number = null;
    protected nResults$: BehaviorSubject<number> = new BehaviorSubject<number>(this.nResults);

    protected results:  TargetingResultModel[] = null;
    protected results$: BehaviorSubject<TargetingResultModel[]> = new BehaviorSubject<TargetingResultModel[]>(this.results);

    protected sortOrder: SortOrderEnum = SortOrderEnum.descending;
    protected sortOrder$: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(this.sortOrder);

    protected targetingSortFields$: BehaviorSubject<LocalizedEnum[]> = new BehaviorSubject<LocalizedEnum[]>([]);

    protected outletSortFields: LocalizedEnum[];

    protected contactSortFields: LocalizedEnum[];

    protected sortField: LocalizedEnum = null;
    protected sortField$: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.sortField);

    protected contactNameFilter: FilterTypeTextModel                           = TargetingFilterFactory.createFilter(TargetingFiltersEnum.contactName) as FilterTypeTextModel;
    protected outletNameFilter: FilterTypeTextModel                            = TargetingFilterFactory.createFilter(TargetingFiltersEnum.outletName) as FilterTypeTextModel;
    protected jobRoleFilter: FilterTypeCheckboxModel<number>                   = TargetingFilterFactory.createFilter(TargetingFiltersEnum.jobRole) as FilterTypeCheckboxModel;
    protected jobTitleFilter: FilterTypeTextModel                              = TargetingFilterFactory.createFilter(TargetingFiltersEnum.jobTitle) as FilterTypeTextModel;
    protected locationFilter: FilterGroupModel                                 = TargetingFilterFactory.createFilter(TargetingFiltersEnum.location) as FilterGroupModel;
    protected countryFilter: FilterTypeCheckboxModel<number>                   = TargetingFilterFactory.createFilter(TargetingFiltersEnum.country) as FilterTypeCheckboxModel;
    protected provinceFilter: FilterTypeCheckboxModel<number>                  = TargetingFilterFactory.createFilter(TargetingFiltersEnum.province) as FilterTypeCheckboxModel;
    protected cityFilter: FilterTypeCheckboxModel<number>                      = TargetingFilterFactory.createFilter(TargetingFiltersEnum.city) as FilterTypeCheckboxModel;
    protected subjectFilter: FilterTypeCheckboxModel<number>                   = TargetingFilterFactory.createFilter(TargetingFiltersEnum.subject) as FilterTypeCheckboxModel;
    protected languageFilter: FilterTypeCheckboxModel<string>                  = TargetingFilterFactory.createFilter(TargetingFiltersEnum.language) as FilterTypeCheckboxModel;
    protected targetAudienceRegionFilter: FilterGroupModel                     = TargetingFilterFactory.createFilter(TargetingFiltersEnum.targetAudienceRegion) as FilterGroupModel;
    protected aocCountryFilter: FilterTypeCheckboxModel<number>                = TargetingFilterFactory.createFilter(TargetingFiltersEnum.aocCountry) as FilterTypeCheckboxModel;
    protected aocProvinceFilter: FilterTypeCheckboxModel<number>               = TargetingFilterFactory.createFilter(TargetingFiltersEnum.aocProvince) as FilterTypeCheckboxModel;
    protected aocCityFilter: FilterTypeCheckboxModel<number>                   = TargetingFilterFactory.createFilter(TargetingFiltersEnum.aocCity) as FilterTypeCheckboxModel;
    protected mediaMarketFilter: FilterTypeCheckboxModel<number>               = TargetingFilterFactory.createFilter(TargetingFiltersEnum.mediaMarket) as FilterTypeCheckboxModel;
    protected coverageFocusFilter: FilterTypeCheckboxModel<string>             = TargetingFilterFactory.createFilter(TargetingFiltersEnum.coverageFocus) as FilterTypeCheckboxModel;
    protected audienceReachFilter: ReachFilterModel                            = TargetingFilterFactory.createFilter(TargetingFiltersEnum.audienceReach) as ReachFilterModel;
    protected marketSectorFilter: FilterTypeCheckboxModel<string>              = TargetingFilterFactory.createFilter(TargetingFiltersEnum.marketSector) as FilterTypeCheckboxModel;
    protected mediaTypeFilter: FilterTypeCheckboxModel<TargetingMediaTypeEnum> = TargetingFilterFactory.createFilter(TargetingFiltersEnum.mediaType) as FilterTypeCheckboxModel;
    protected productTypeFilter: FilterTypeCheckboxModel<string>               = TargetingFilterFactory.createFilter(TargetingFiltersEnum.productType) as FilterTypeCheckboxModel;
    protected productFrequencyFilter: FilterTypeCheckboxModel<string>          = TargetingFilterFactory.createFilter(TargetingFiltersEnum.productFrequency) as FilterTypeCheckboxModel;
    protected targetAudienceFilter: FilterTypeCheckboxModel<string>            = TargetingFilterFactory.createFilter(TargetingFiltersEnum.targetAudience) as FilterTypeCheckboxModel;
    protected statusFilter: StatusFilterModel                                  = TargetingFilterFactory.createFilter(TargetingFiltersEnum.status) as StatusFilterModel;
    protected listFilter: FilterTypeCheckboxModel<number>                      = TargetingFilterFactory.createFilter(TargetingFiltersEnum.list) as FilterTypeCheckboxModel;
    protected distributionFilter: FilterTypeCheckboxModel<number>              = TargetingFilterFactory.createFilter(TargetingFiltersEnum.distribution) as FilterTypeCheckboxModel;
    protected streamContentFilter: FilterTypeStreamContentCheckboxModel        = TargetingFilterFactory.createFilter(TargetingFiltersEnum.streamContent) as FilterTypeStreamContentCheckboxModel;
    protected emailFilter: FilterTypeTextModel                                 = TargetingFilterFactory.createFilter(TargetingFiltersEnum.email) as FilterTypeTextModel;
    protected deliveryStatusFilter: FilterTypeCheckboxModel<string>            = TargetingFilterFactory.createFilter(TargetingFiltersEnum.deliveryStatus) as FilterTypeCheckboxModel;

    protected allFilters: FilterTypeModel[];

    protected primaryFacets: TargetingFacetCollection = null;
    protected primaryFacets$: BehaviorSubject<TargetingFacetCollection> = new BehaviorSubject<TargetingFacetCollection>(this.primaryFacets);

    protected filteredFacets: TargetingFacetCollection = null;
    protected filteredFacets$: BehaviorSubject<TargetingFacetCollection> = new BehaviorSubject<TargetingFacetCollection>(this.filteredFacets);

    protected mainSearchRequest: Subscription;
    protected primaryFacetRequest: Subscription;
    protected filteredFacetRequest: Subscription;
    protected fullEntityRequest: Subscription;

    jobRoles: JobRoleModel[];
    mediaTypes: LocalizedEnum[];
    productTypes: LocalizedEnum[];
    marketSectors: LocalizedEnum[];
    coverageFocuses: LocalizedEnum[];
    productFrequencies: LocalizedEnum[];
    targetAudiences: LocalizedEnum[];
    languages: LocalizedEnum[];
    countries: LocalizedEnum[];
    provinces: LocalizedEnum[];
    circulationTypes: LocalizedEnum[];
    targetingLists: TargetingListList[];
    rectangleQuery: RectMapQuery;
    userPreferenceTimezone: string;

    page: number = 1;
    totalNumberOfPages: number = 1;
    pagingValidator: () => Observable<boolean>;

    constructor(
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected targetingResultFactory: TargetingResultFactory,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionApiService: DistributionApi,
    ) {
        super();

        this.locationFilter.children.push(this.countryFilter);
        this.locationFilter.children.push(this.provinceFilter);
        this.locationFilter.children.push(this.cityFilter);

        this.targetAudienceRegionFilter.children.push(this.aocCountryFilter);
        this.targetAudienceRegionFilter.children.push(this.aocProvinceFilter);
        this.targetAudienceRegionFilter.children.push(this.aocCityFilter);
        this.targetAudienceRegionFilter.children.push(this.mediaMarketFilter);

        this.allFilters = this.getFilterDefinitions();
        this.setFilters(this.allFilters);

        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => this.userPreferenceTimezone = preferences?.timeZone
            )
        );

        if (this.allFilters.find(x => x === this.jobRoleFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getAllJobRoles().subscribe(
                jobRoles => {
                    this.jobRoles = jobRoles;
                    this.getJobRoleOptions();
                }
            ));
        }
        if (this.allFilters.find(x => x === this.audienceReachFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getCirculationTypes().subscribe(
                circulationTypes => {
                    this.circulationTypes = circulationTypes;
                    this.audienceReachFilter.setCirculationTypes(circulationTypes);
                    this.audienceReachFilter.setValue(this.audienceReachFilter.getValue());
                }
            ));
        }
        if (this.allFilters.find(x => x === this.mediaTypeFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getMediaTypes().subscribe(
                mediaTypes => {
                    this.mediaTypes = mediaTypes;
                    this.getMediaTypeOptions();
                }
            ));
        }
        if (this.allFilters.find(x => x === this.productTypeFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getProductTypes().subscribe(
                productTypes => {
                    this.productTypes = productTypes;
                    this.getProductTypeOptions();
                }
            ));
        }
        if (this.allFilters.find(x => x === this.marketSectorFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getMarketSectors().subscribe(
                marketSectors => {
                    this.marketSectors = marketSectors;
                    this.getMarketSectorOptions();
                }
            ));
        }
        if (this.allFilters.find(x => x === this.coverageFocusFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getCoverageFocuses().subscribe(
                coverageFocuses => {
                    this.coverageFocuses = coverageFocuses;
                    this.getCoverageFocusOptions();
                }
            ));
        }
        if (this.allFilters.find(x => x === this.productFrequencyFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getProductFrequencies().subscribe(
                productFrequencies => {
                    this.productFrequencies = productFrequencies;
                    this.getProductFrequencyOptions();
                }
            ));
        }
        if (this.allFilters.find(x => x === this.targetAudienceFilter)) {
            this.subscriptions.push(this.journodexEnumsService.getTargetAudiences().subscribe(
                targetAudiences => {
                    this.targetAudiences = targetAudiences;
                    this.getTargetAudienceOptions();
                }
            ));
        }
        if (this.allFilters.find(x => x === this.languageFilter)) {
            this.languages = [];
            const availableLanguages: LanguageModel[] = this.localizationService.availableLanguages;
            for (let i = 0; i < availableLanguages.length; i++) {
                this.languages.push({
                    enumValue: availableLanguages[i].id.toUpperCase(),
                    displayString: this.localizationService.transform(availableLanguages[i].name),
                });
            }
            this.getLanguageOptions();
        }
    }

    abstract doSearch(refreshPrimaryFacets: boolean, refreshFilteredFacets: boolean, updateMap?: boolean): Observable<TargetingSearchResponseApiModel> | void;
    abstract getFilterDefinitions(): FilterTypeModel[];

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }
    /**
     * Gets the total number of items that can be paged through
     */
    getItemCount(): Observable<number> {
        return this.nResults$;
    }

    /**
     * Gets the current page criteria
     */
    getPageCriteria(): Observable<PageCriteriaModelInterface> {
        return this.pageCriteria$;
    }

    public setMaxPageCriteria(totalNumberOfPages: number): void {
        this.totalNumberOfPages = totalNumberOfPages;
    }

    /**
     * Updates the page criteria
     */
    setPageCriteria(pageCriteria: PageCriteriaModelInterface, search: boolean = true): void {
        this.page = pageCriteria.page;
        if (this.page <= this.totalNumberOfPages) {
            this.pageCriteria = pageCriteria;
            this.pageCriteria$.next(pageCriteria);
            if (search) {
                this.doSearch(false, false, false);
            }
        }
    }

    /**
     * Checks that the page criteria can be updated
     * @NOTE This is an observable so that users can make the decision via dialogs etc.
     */
    public registerPagingValidator(validator: () => Observable<boolean>): void {
        this.pagingValidator = validator;
    }

    public canSetPageCriteria(): Observable<boolean> {
        if (this.pagingValidator) {
            return this.pagingValidator();
        }
        return of(true);
    }

    getResults(): Observable<TargetingResultModel[]> {
        return this.results$;
    }

    clearSearch(): void {
        this.nResults = null;
        this.nResults$.next(this.nResults);
        this.results = null;
        this.results$.next(this.results);
        this.searchLoading$.next(false);
    }

    async setSearchResults(response: TargetingSearchResponseApiModel): Promise<void> {
        this.nResults = response.numResults;
        this.nResults$.next(this.nResults);
        const resultPromises = response.results.map(x => this.targetingResultFactory.createTargetingResult(x));
        this.results = await promiseOnlyResolved(resultPromises);
        this.results$.next(this.results);
        this.fetchListMemberships();
        this.searchLoading$.next(false);
    }

    // Fast search is just the results without any facets
    fastSearch(search: TargetingSearch | TargetingList, filters?: TargetingSearch): Observable<any> {
        const rows = this.pageCriteria.pageSize;
        const start = this.pageCriteria.page > 0 ? (this.pageCriteria.page - 1) * rows : 0;
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;

        let searchRequest: Observable<TargetingSearchResponseApiModel>;

        if (search instanceof TargetingSearch) {

            if (search.isRecommendedRecipientSearch) {
                searchRequest = this.distributionApiService.performRecommendedRecipientSearch(
                    search.toApiModel(false, filters).query,
                    start,
                    rows,
                    sortOrder,
                    sortField
                );
            } else {
                searchRequest = this.targetingSearchesApiService.performSearch(
                    search.toApiModel(false, filters).query,
                    start,
                    rows,
                    sortOrder,
                    sortField
                );
            }

        } else if (search instanceof TargetingList) {
            // TODO: Pull out common list api conversions
            const listQuery = new TargetingSearch();
            listQuery.rectangleQuery = this.rectangleQuery;
            listQuery.list.include = [search.id];
            if (filters) {
                filters.rectangleQuery = this.rectangleQuery;
            }
            const apiQuery = listQuery.toApiModel(false, filters).query;
            apiQuery.includeGeoRestrictions = false;

            searchRequest = this.targetingSearchesApiService.performSearch(
                apiQuery,
                start,
                rows,
                sortOrder,
                sortField
            );
        }

        return searchRequest.pipe(
            map(async response => {
                await this.setSearchResults(response);
                return response;
            })
        );
    }

    facetSearch(search: TargetingSearch | TargetingList, filters?: TargetingSearch): Observable<TargetingFacetCollection> {
        const sortOrder = this.sortOrder;
        const sortField = this.sortField ? this.sortField.enumValue : null;
        let searchRequest: Observable<TargetingSearchResponseApiModel>;
        if (search instanceof TargetingSearch) {
            if (search.isRecommendedRecipientSearch) {
                searchRequest = this.distributionApiService.performRecommendedRecipientSearch(
                    search.toApiModel(true, filters).query,
                    0,
                    0,
                    sortOrder,
                    sortField
                );
            } else {
                searchRequest = this.targetingSearchesApiService.performSearch(
                    search.toApiModel(true, filters).query,
                    0,
                    0,
                    sortOrder,
                    sortField
                );
            }
        } else if (search instanceof TargetingList) {
            // TODO: Pull out common list api conversions
            const listQuery = new TargetingSearch();
            listQuery.list.include = [search.id];
            const apiQuery = listQuery.toApiModel(true, filters).query;
            apiQuery.includeGeoRestrictions = false;

            searchRequest = this.targetingSearchesApiService.performSearch(
                apiQuery,
                0,
                0,
                sortOrder,
                sortField
            );
        }

        return searchRequest?.pipe(map(response => {
            return this.parseFacets(response.facetResults);
        }));
    }

    parseFacets(facets: FacetResult[]): TargetingFacetCollection {
        const facetCollection: TargetingFacetCollection = {
            facets: new Map<TargetingSearchFacetsEnum, FacetValueModel[]>(),
        };

        facets.forEach(facet => {
            facetCollection.facets.set(facet.facet, facet.counts);
        });

        return facetCollection;
    }

    public getPrimaryFacets(): Observable<TargetingFacetCollection> {
        return this.primaryFacets$;
    }
    public getFilteredFacets(): Observable<TargetingFacetCollection> {
        return this.filteredFacets$;
    }
    protected setPrimaryFacets(collection: TargetingFacetCollection): void {
        if (collection) {
            this.primaryFacets = {
                facets: new Map(collection.facets),
            };
        } else {
            this.primaryFacets = null;
        }
        this.primaryFacets$.next(this.primaryFacets);
        this.setFilterOptions();
    }

    protected setFilteredFacets(collection: TargetingFacetCollection): void {
        if (collection) {
            this.filteredFacets = {
                facets: new Map(collection.facets),
            };
        } else {
            this.filteredFacets = null;
        }
        this.filteredFacets$.next(this.filteredFacets);
    }

    getSortableFields(): Observable<any[]> {
        return this.targetingSortFields$;
    }
    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrder$;
    }
    getSortField(): Observable<any> {
        return this.sortField$;
    }
    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.refreshResults();
        }
    }
    setSortField(field: any): void {
        if (field !== this.sortField) {
            this.sortField = field;
            this.sortField$.next(field);
            this.refreshResults();
        }
    }

    refreshResults(): void {
        this.doSearch(false, false);
    }

    getSearchLoading(): Observable<boolean> {
        return this.searchLoading$;
    }

    getFiltersLoading(): Observable<boolean> {
        return this.filtersLoading$;
    }

    fetchListMemberships() {
        if (this.results && this.results.length > 0) {
            this.targetingListsApiService.getListMemberships(this.results.map(x => {
                return <EntityIdApiModel> {
                    contactUuid : x.entityType === EntityType.CONTACT ? x.id : null,
                    outletUuid : x.entityType === EntityType.OUTLET ? x.id : null
                };
            })).subscribe(listMemberships => {
                for (let i = 0; i < this.results?.length; i++) {
                    const resultMemberships = listMemberships.find(memberships => memberships.uuid === this.results[i]?.id);
                    if (resultMemberships) {
                        this.applyListMembershipsToResult(this.results[i], resultMemberships);
                    }
                }
                this.results$.next(this.results);
                this.searchLoading$.next(false);
            });
        } else {
            this.results$.next(this.results);
            this.searchLoading$.next(false);
        }
    }

    applyListMembershipsToResult(result: TargetingResultModel, listMemberships: ListMembershipApiModel) {
        // The api returns listmemberships in the same order as they were requested
        // But it may be a bit sturdier to map using the uuid.
        if (result.id !== listMemberships?.uuid) {
            console.log('Unexpected list memberships behavior; results not ordered');
        }

        result.listMemberships = listMemberships?.targetingLists?.map(x => new TargetingList(x));
    }

    //#region filters

    // Has to be called from list service to avoid circular dep
    addListsToFilters(lists: TargetingListList[]) {
        this.listFilter.options = lists.map(list => {
            return {
                label: list.name,
                value: list.id
            };
        });
    }

    // Has to be called from distribution service to avoid circular dep
    addDistributionsToFilters(dists: DistributionSummaryModel[]) {
        this.distributionFilter.options = dists.map(list => {
            return {
                label: list.name,
                value: list.id
            };
        });
    }

    /**
     * Applies the current filters onto the search
     */
    applyFilters(search: boolean = true): void {
        super.applyFilters();

        this.setPageCriteria({page: 1, pageSize: this.pageCriteria.pageSize}, false);

        if (this.audienceReachFilter.value && this.audienceReachFilter.value.type && this.sortField) {
            if (Array.from(circulationTypeToSortType.values()).find(x => x === this.sortField.enumValue)) {

                const sortField = this.outletSortFields?.find(x =>
                    x.enumValue === circulationTypeToSortType.get(this.audienceReachFilter.value.type)
                );
                if (sortField) {
                    this.setSortField(sortField);
                }
            }
        }

        if (search) {
            this.doSearch(false, true);
        }
    }

    hasFiltersToApply(): boolean {
        return (
            this.contactNameFilter.getValue().length > 0
            || this.outletNameFilter.getValue().length > 0
            || this.subjectFilter.getValue().length > 0
            || this.mediaTypeFilter.getValue().length > 0
            || this.productTypeFilter.getValue().length > 0
            || this.marketSectorFilter.getValue().length > 0
            || this.aocCountryFilter.getValue().length > 0
            || this.aocProvinceFilter.getValue().length > 0
            || this.aocCityFilter.getValue().length > 0
            || this.mediaMarketFilter.getValue().length > 0
            || this.coverageFocusFilter.getValue().length > 0
            || this.productFrequencyFilter.getValue().length > 0
            || this.targetAudienceFilter.getValue().length > 0
            || this.languageFilter.getValue().length > 0
            || this.countryFilter.getValue().length > 0
            || this.provinceFilter.getValue().length > 0
            || this.cityFilter.getValue().length > 0
            || !isNaN(this.audienceReachFilter.getValue().range.min)
            || !isNaN(this.audienceReachFilter.getValue().range.max)
            || this.jobRoleFilter.getValue().length > 0
            || this.jobTitleFilter.getValue().length > 0
            || this.statusFilter.hasFiltersToApply()
            || this.listFilter.getValue().length > 0
            || this.distributionFilter.getValue().length > 0
            || this.deliveryStatusFilter.getValue().length > 0
        );
    }

    generateFiltersSearch(): TargetingSearch {
        let filters = new TargetingSearch();
        filters.contactIdsNames[this.contactNameFilter.includeExclude] = this.contactNameFilter.getValue().map(contact => new IdNamePairModel(contact));
        filters.outletIdsNames[this.outletNameFilter.includeExclude] = this.outletNameFilter.getValue().map(outlet => new IdNamePairModel(outlet));
        filters.subjects[this.subjectFilter.includeExclude] = this.subjectFilter.getValue();
        filters.mediaTypes[this.mediaTypeFilter.includeExclude] = this.mediaTypeFilter.getValue();
        filters.productTypes[this.productTypeFilter.includeExclude] = this.productTypeFilter.getValue();
        filters.marketSectors[this.marketSectorFilter.includeExclude] = this.marketSectorFilter.getValue();
        filters.areasOfCoverage[this.aocCountryFilter.includeExclude].push(...this.aocCountryFilter.getValue());
        filters.areasOfCoverage[this.aocProvinceFilter.includeExclude].push(...this.aocProvinceFilter.getValue());
        filters.areasOfCoverage[this.aocCityFilter.includeExclude].push(...this.aocCityFilter.getValue());
        filters.mediaMarkets[this.mediaMarketFilter.includeExclude] = this.mediaMarketFilter.getValue();
        filters.coverageFocuses[this.coverageFocusFilter.includeExclude] = this.coverageFocusFilter.getValue();
        filters.productFrequencies[this.productFrequencyFilter.includeExclude] = this.productFrequencyFilter.getValue();
        filters.targetAudiences[this.targetAudienceFilter.includeExclude] = this.targetAudienceFilter.getValue();
        filters.languages[this.languageFilter.includeExclude] = this.languageFilter.getValue();
        filters.jobRoles[this.jobRoleFilter.includeExclude] = this.jobRoleFilter.getValue();
        filters.jobTitles[this.jobTitleFilter.includeExclude] = this.jobTitleFilter.getValue();
        filters.audienceReachFilter = this.audienceReachFilter.getValue().type ? this.audienceReachFilter.getValue() : null;
        filters.geographies[this.countryFilter.includeExclude].push(...this.countryFilter.getValue());
        filters.geographies[this.provinceFilter.includeExclude].push(...this.provinceFilter.getValue());
        filters.geographies[this.cityFilter.includeExclude].push(...this.cityFilter.getValue());
        filters.list[this.listFilter.includeExclude] = this.listFilter.getValue();
        filters.distribution[this.distributionFilter.includeExclude] = this.distributionFilter.getValue();
        filters = this.generateStatusFilters(filters);

        return filters;
    }

    generateStatusFilters(filters: TargetingSearch): TargetingSearch {
        const statusFilterValue = this.statusFilter.getValue();
        if (statusFilterValue.includePrivateContacts) {
            filters.includePrivateContacts = true;
            filters.privateContactsOnly = !statusFilterValue.includeSystemContacts;
        } else {
            filters.includePrivateContacts = false;
        }
        filters.badgeFilters = [];
        if (statusFilterValue.createdDate) {
            filters.badgeFilters.push({
                type: BadgeFilterTypesEnum.NEW_ENTITY,
                since: moment.tz(statusFilterValue.createdDate.toString(), timezoneConversionDateFormat, this.userPreferenceTimezone).toDate(),
                before: null
            });
        }
        if (statusFilterValue.updatedDate) {
            filters.badgeFilters.push({
                type: BadgeFilterTypesEnum.UPDATED,
                since: moment.tz(statusFilterValue.updatedDate.toString(), timezoneConversionDateFormat, this.userPreferenceTimezone).toDate(),
                before: null
            });
        }
        // Recently Verified filter hidden for now (10-1)
        // if (statusFilterValue.verifiedDate) {
        //     filters.badgeFilters.push({type: BadgeFilterTypesEnum.VERIFIED, since: statusFilterValue.verifiedDate, before: null});
        // }
        if (statusFilterValue.newEmploymentDate) {
            filters.badgeFilters.push({
                type: BadgeFilterTypesEnum.NEW_EMPLOYMENT,
                since: moment.tz(statusFilterValue.newEmploymentDate.toString(), timezoneConversionDateFormat, this.userPreferenceTimezone).toDate(),
                before: null
            });
        }
        if (statusFilterValue.isLongstanding) {
            filters.badgeFilters.push({type: BadgeFilterTypesEnum.LONGSTANDING, since: null, before: moment().subtract(10, 'years').toDate()});
        }
        if (statusFilterValue.hasMultipleEmployments) {
            filters.badgeFilters.push({type: BadgeFilterTypesEnum.MUTLIPLE_EMPLOYMENTS, since: null, before: null});
        }
        if (statusFilterValue.isAiReference) {
            filters.badgeFilters.push({type: BadgeFilterTypesEnum.AI_REFERENCE, since: null, before: null});
        }
        filters.hasActiveNotices = statusFilterValue.hasActiveNotices;

        return filters;
    }

    populateFiltersFromSearch(filters: TargetingSearch): void {
        if (filters.contactIdsNames.include.length > 0) {
            this.contactNameFilter.setValue(filters.contactIdsNames.include.map(contact => contact.name));
            this.contactNameFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.contactIdsNames.exclude.length) {
            this.contactNameFilter.setValue(filters.contactIdsNames.exclude.map(contact => contact.name));
            this.contactNameFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (filters.outletIdsNames.include.length > 0) {
            this.outletNameFilter.setValue(filters.outletIdsNames.include.map(outlet => outlet.name));
            this.outletNameFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.outletIdsNames.exclude.length) {
            this.outletNameFilter.setValue(filters.outletIdsNames.exclude.map(outlet => outlet.name));
            this.outletNameFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (filters.subjects.include.length > 0) {
            this.subjectFilter.setValue(filters.subjects.include);
            this.subjectFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.subjects.exclude.length) {
            this.subjectFilter.setValue(filters.subjects.exclude);
            this.subjectFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getSubjectOptions();

        if (filters.mediaTypes.include.length > 0) {
            this.mediaTypeFilter.setValue(filters.mediaTypes.include);
            this.mediaTypeFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.mediaTypes.exclude.length) {
            this.mediaTypeFilter.setValue(filters.mediaTypes.exclude);
            this.mediaTypeFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getMediaTypeOptions();

        if (filters.productTypes.include.length > 0) {
            this.productTypeFilter.setValue(filters.productTypes.include);
            this.productTypeFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.productTypes.exclude.length) {
            this.productTypeFilter.setValue(filters.productTypes.exclude);
            this.productTypeFilter.setIncludeExclude(IncludeExcludeEnum.exclude);

        }
        this.getProductTypeOptions();

        if (filters.marketSectors.include.length > 0) {
            this.marketSectorFilter.setValue(filters.marketSectors.include);
            this.marketSectorFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.marketSectors.exclude.length) {
            this.marketSectorFilter.setValue(filters.marketSectors.exclude);
            this.marketSectorFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getMarketSectorOptions();

        if (filters.coverageFocuses.include.length > 0) {
            this.coverageFocusFilter.setValue(filters.coverageFocuses.include);
            this.coverageFocusFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.coverageFocuses.exclude.length) {
            this.coverageFocusFilter.setValue(filters.coverageFocuses.exclude);
            this.coverageFocusFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getCoverageFocusOptions();

        if (filters.productFrequencies.include.length > 0) {
            this.productFrequencyFilter.setValue(filters.productFrequencies.include);
            this.productFrequencyFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.productFrequencies.exclude.length){
            this.productFrequencyFilter.setValue(filters.productFrequencies.exclude);
            this.productFrequencyFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getProductFrequencyOptions();

        if (filters.targetAudiences.include.length > 0) {
            this.targetAudienceFilter.setValue(filters.targetAudiences.include);
            this.targetAudienceFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.targetAudiences.exclude.length) {
            this.targetAudienceFilter.setValue(filters.targetAudiences.exclude);
            this.targetAudienceFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getTargetAudienceOptions();

        if (filters.languages.include.length > 0) {
            this.languageFilter.setValue(filters.languages.include.map(x => x.toUpperCase()));
            this.languageFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.languages.exclude.length) {
            this.languageFilter.setValue(filters.languages.exclude.map(x => x.toUpperCase()));
            this.languageFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getLanguageOptions();

        this.setLocationFiltersValue(filters.geographies);
        this.getLocationOptions(TargetingSearchFacetsEnum.country);
        this.getLocationOptions(TargetingSearchFacetsEnum.province);
        this.getLocationOptions(TargetingSearchFacetsEnum.city);

        this.setTargetAudienceRegionFiltersValue(filters.areasOfCoverage, filters.mediaMarkets);
        this.getTargetAudienceRegionOptions();

        if (filters.jobRoles.include.length > 0) {
            this.jobRoleFilter.setValue(filters.jobRoles.include);
            this.jobRoleFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.jobRoles.exclude.length) {
            this.jobRoleFilter.setValue(filters.jobRoles.exclude);
            this.jobRoleFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }
        this.getJobRoleOptions();

        if (filters.jobTitles.include.length > 0) {
            this.jobTitleFilter.setValue(filters.jobTitles.include);
            this.jobTitleFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.jobTitles.exclude.length) {
            this.jobTitleFilter.setValue(filters.jobTitles.exclude);
            this.jobTitleFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (filters.audienceReachFilter) {
            this.audienceReachFilter.setValue(filters.audienceReachFilter);
        }

        this.locationFilter.setValue([]);

        if (filters.list.include.length > 0) {
            this.listFilter.setValue(filters.list.include);
            this.listFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else if (filters.list.exclude.length) {
            this.listFilter.setValue(filters.list.exclude);
            this.listFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        if (filters.distribution.include.length > 0) {
            this.distributionFilter.setValue(filters.distribution.include);
            this.distributionFilter.setIncludeExclude(IncludeExcludeEnum.include);
        } else  if (filters.distribution.exclude.length) {
            this.distributionFilter.setValue(filters.distribution.exclude);
            this.distributionFilter.setIncludeExclude(IncludeExcludeEnum.exclude);
        }

        this.populateStatusFiltersFromSearch(filters);
    }

    populateStatusFiltersFromSearch(filters: TargetingSearch): void {
        let userPreferenceTimeString;
        const value = new StatusFilterData();
        value.includePrivateContacts = filters.includePrivateContacts;
        value.includeSystemContacts = !filters.privateContactsOnly;
        const createdDate = filters.badgeFilters.find(x => x.type === BadgeFilterTypesEnum.NEW_ENTITY);
        if (createdDate) {
            userPreferenceTimeString = moment(createdDate.since).tz(this.userPreferenceTimezone).format(timezoneConversionDateFormat);
            value.createdDate = moment(userPreferenceTimeString).toDate();
        }
        const updatedDate = filters.badgeFilters.find(x => x.type === BadgeFilterTypesEnum.UPDATED);
        if (updatedDate) {
            userPreferenceTimeString = moment(updatedDate.since).tz(this.userPreferenceTimezone).format(timezoneConversionDateFormat);
            value.updatedDate = moment(userPreferenceTimeString).toDate();
        }
        const newEmploymentDate = filters.badgeFilters.find(x => x.type === BadgeFilterTypesEnum.NEW_EMPLOYMENT);
        if (newEmploymentDate) {
            userPreferenceTimeString = moment(newEmploymentDate.since).tz(this.userPreferenceTimezone).format(timezoneConversionDateFormat);
            value.newEmploymentDate = moment(userPreferenceTimeString).toDate();
        }
        const isLongstanding = filters.badgeFilters.find(x => x.type === BadgeFilterTypesEnum.LONGSTANDING);
        value.isLongstanding = !!isLongstanding;
        const hasMultipleEmployments = filters.badgeFilters.find(x => x.type === BadgeFilterTypesEnum.MUTLIPLE_EMPLOYMENTS);
        value.hasMultipleEmployments = !!hasMultipleEmployments;
        value.hasActiveNotices = !!filters.hasActiveNotices;

        const isAiReference = filters.badgeFilters.find(x => x.type === BadgeFilterTypesEnum.AI_REFERENCE);
        value.isAiReference = !!isAiReference;

        this.statusFilter.setValue(value);
    }

    setFilterOptions(): void {
        if (!this.primaryFacets) {
            this.mediaTypeFilter.loading = true;
            this.subjectFilter.loading = true;

            return;
        }
        this.primaryFacets.facets.forEach((value: FacetValueModel[], type: TargetingSearchFacetsEnum) => {
            switch (type) {
                case TargetingSearchFacetsEnum.subject:
                    this.getSubjectOptions();
                    break;
                case TargetingSearchFacetsEnum.mediaType:
                    this.getMediaTypeOptions();
                    break;
                case TargetingSearchFacetsEnum.productType:
                    this.getProductTypeOptions();
                    break;
                case TargetingSearchFacetsEnum.marketSector:
                    this.getMarketSectorOptions();
                    break;
                case TargetingSearchFacetsEnum.coverageFocus:
                    this.getCoverageFocusOptions();
                    break;
                case TargetingSearchFacetsEnum.productFrequency:
                    this.getProductFrequencyOptions();
                    break;
                case TargetingSearchFacetsEnum.targetAudience:
                    this.getTargetAudienceOptions();
                    break;
                case TargetingSearchFacetsEnum.language:
                    this.getLanguageOptions();
                    break;
                case TargetingSearchFacetsEnum.country:
                    this.getLocationOptions(TargetingSearchFacetsEnum.country);
                    break;
                case TargetingSearchFacetsEnum.province:
                    this.getLocationOptions(TargetingSearchFacetsEnum.province);
                    break;
                case TargetingSearchFacetsEnum.city:
                    this.getLocationOptions(TargetingSearchFacetsEnum.city);
                    break;
                case TargetingSearchFacetsEnum.targetAudienceRegion:
                    this.getTargetAudienceRegionOptions();
                    break;
                case TargetingSearchFacetsEnum.jobRole:
                    this.getJobRoleOptions();
                    break;
            }
        });

        this.setFilters(this.filters);
    }

    protected setLocationFiltersValue(value: IncludeExcludeModel<number>) {
        const ids = [...value.include, ...value.exclude];
        const countries = [], provinces = [], cities = [];
        this.journodexRegionService.getByGeographyIds(ids).subscribe(locations => {
            locations.forEach(location => {
                switch (location.geographicType) {
                    case GeographyTypesEnum.country:
                        countries.push(location.collectionId);
                        break;
                    case GeographyTypesEnum.province:
                        provinces.push(location.collectionId);
                        break;
                    case GeographyTypesEnum.city:
                        cities.push(location.collectionId);
                        break;
                }
            });

            this.countryFilter.setValue(countries);
            const countryInExclude = intersection(value.include, countries).length > 0
                ? IncludeExcludeEnum.include : intersection(value.exclude, countries).length > 0 ? IncludeExcludeEnum.exclude : IncludeExcludeEnum.include;
            this.countryFilter.setIncludeExclude(countryInExclude);

            this.provinceFilter.setValue(provinces);
            const provinceInExclude = intersection(value.include, provinces).length > 0
                ? IncludeExcludeEnum.include : intersection(value.exclude, provinces).length > 0 ? IncludeExcludeEnum.exclude : IncludeExcludeEnum.include;
            this.provinceFilter.setIncludeExclude(provinceInExclude);

            this.cityFilter.setValue(cities);
            const cityInExclude = intersection(value.include, cities).length > 0
                ? IncludeExcludeEnum.include : intersection(value.exclude, cities).length > 0 ? IncludeExcludeEnum.exclude : IncludeExcludeEnum.include;
            this.cityFilter.setIncludeExclude(cityInExclude);
        });
    }

    protected setTargetAudienceRegionFiltersValue(areaOfCoverage: IncludeExcludeModel<number>, mediaMarket: IncludeExcludeModel<number>) {
        const aocIds = [...areaOfCoverage.include, ...areaOfCoverage.exclude];
        const countries = [], provinces = [], cities = [], mediaMarkets = [];
        this.journodexRegionService.getByGeographyIds(aocIds).subscribe(areasOfCoverage => {
            areasOfCoverage.forEach(aoc => {
                switch (aoc.geographicType) {
                    case GeographyTypesEnum.country:
                        countries.push(aoc.collectionId);
                        break;
                    case GeographyTypesEnum.province:
                        provinces.push(aoc.collectionId);
                        break;
                    case GeographyTypesEnum.city:
                        cities.push(aoc.collectionId);
                        break;
                }
            });
            this.aocCountryFilter.setValue(countries);
            const countryInExclude = intersection(areaOfCoverage.include, countries).length > 0
                ? IncludeExcludeEnum.include : IncludeExcludeEnum.exclude;
            this.aocCountryFilter.setIncludeExclude(countryInExclude);

            this.aocProvinceFilter.setValue(provinces);
            const provinceInExclude = intersection(areaOfCoverage.include, provinces).length > 0
                ? IncludeExcludeEnum.include : IncludeExcludeEnum.exclude;
            this.aocProvinceFilter.setIncludeExclude(provinceInExclude);

            this.aocCityFilter.setValue(cities);
            const cityInExclude = intersection(areaOfCoverage.include, cities).length > 0
                ? IncludeExcludeEnum.include : IncludeExcludeEnum.exclude;
            this.aocCityFilter.setIncludeExclude(cityInExclude);
        });

        const mmIds = [...mediaMarket.include, ...mediaMarket.exclude];
        this.journodexEnumsService.getMediaMarketsByIds(mmIds).subscribe(returnedMediaMarkets => {
            returnedMediaMarkets.forEach(mm => mediaMarkets.push(mm));
            this.mediaMarketFilter.setValue(mediaMarkets);
            const mediaMarketInExclude = intersection(mediaMarket.include, mediaMarkets).length > 0
                ? IncludeExcludeEnum.include : IncludeExcludeEnum.exclude;
            this.mediaMarketFilter.setIncludeExclude(mediaMarketInExclude);
        });
    }

    protected getEnumOptions(filterCheckbox: FilterTypeCheckboxModel<string>, facetType: TargetingSearchFacetsEnum, enumOptions: LocalizedEnum[]): void {
        if (!enumOptions) {
            return;
        }
        const newOptions = [];
        let values = filterCheckbox.getValue();
        const facets = this.primaryFacets && this.primaryFacets.facets.get(facetType);
        if (facets) {
            newOptions.push(
                ...facets
                    .filter(facet => ('' + facet.value).trim().length > 0)
                    .map((facet) => {
                        const enumValue = enumOptions.find(mType => mType.enumValue === facet.value);
                        if (!enumValue) {
                            return null;
                        }
                        return {
                            label: enumValue.displayString,
                            value: enumValue.enumValue,
                            count: facet.count,
                        };
                    })
                    .filter(facet => facet)
            );
        }
        if (values && values.length > 0) {
            if (newOptions.length > 0) {
                values = values.filter(value => !newOptions.find(opt => opt.value === value));
            }
            if (values.length > 0) {
                newOptions.push(
                    ...values.map(value => {
                        const enumValue = enumOptions.find(mType => mType.enumValue === value);
                        if (!enumValue) {
                            return null;
                        }
                        return {
                            label: enumValue.displayString,
                            value: enumValue.enumValue
                        };
                    }).filter(facet => facet)
                );
            }
        }
        filterCheckbox.options = newOptions;
        filterCheckbox.setValue(filterCheckbox.getValue()); // updates the chips
        filterCheckbox.loading = false;
    }

    protected getMediaTypeOptions() {
        this.getEnumOptions(
            this.mediaTypeFilter,
            TargetingSearchFacetsEnum.mediaType,
            this.mediaTypes
        );
    }
    protected getProductTypeOptions() {
        this.getEnumOptions(
            this.productTypeFilter,
            TargetingSearchFacetsEnum.productType,
            this.productTypes
        );
    }
    protected getMarketSectorOptions() {
        this.getEnumOptions(
            this.marketSectorFilter,
            TargetingSearchFacetsEnum.marketSector,
            this.marketSectors
        );
    }
    protected getCoverageFocusOptions() {
        this.getEnumOptions(
            this.coverageFocusFilter,
            TargetingSearchFacetsEnum.coverageFocus,
            this.coverageFocuses
        );
    }
    protected getProductFrequencyOptions() {
        this.getEnumOptions(
            this.productFrequencyFilter,
            TargetingSearchFacetsEnum.productFrequency,
            this.productFrequencies
        );
    }
    protected getTargetAudienceOptions() {
        this.getEnumOptions(
            this.targetAudienceFilter,
            TargetingSearchFacetsEnum.targetAudience,
            this.targetAudiences
        );
    }
    protected getLanguageOptions() {
        this.getEnumOptions(
            this.languageFilter,
            TargetingSearchFacetsEnum.language,
            this.languages
        );
    }

    protected getLocationOptions(type: TargetingSearchFacetsEnum) {
        const facets = this.primaryFacets && this.primaryFacets.facets.get(type) || [];
        const ids = facets.map(facet => +facet.value);
        const geographyType = GeographyTypesEnum[type.toLowerCase()];
        this.journodexRegionService.getRegions(ids, geographyType).subscribe(regions => {
            const options = facets
                .filter(val => ('' + val.value).trim().length > 0)
                .map((val) => {
                    const region = regions.find(subj => +subj.id === +val.value);
                    if (!region) {
                        return null;
                    }
                    return {
                        label: region.displayString.substring(0, region.displayString.lastIndexOf('(')),
                        value: region.collectionId,
                        count: val.count,
                    };
                })
                .filter(val => val);

            switch (type) {
                case TargetingSearchFacetsEnum.country:
                    this.countryFilter.options = options;
                    this.countryFilter.setValue(this.countryFilter.getValue());
                    this.countryFilter.loading = false;
                    break;
                case TargetingSearchFacetsEnum.province:
                    this.provinceFilter.options = options;
                    this.provinceFilter.setValue(this.provinceFilter.getValue());
                    this.provinceFilter.loading = false;
                    break;
                case TargetingSearchFacetsEnum.city:
                    this.cityFilter.options = options;
                    this.cityFilter.setValue(this.cityFilter.getValue());
                    this.cityFilter.loading = false;
                    break;
            }
        });
    }

    protected getTargetAudienceRegionOptions() {
        this.aocCountryFilter.options = [];
        this.aocProvinceFilter.options = [];
        this.aocCityFilter.options = [];
        this.mediaMarketFilter.options = [];
        const aocFacets = this.primaryFacets && this.primaryFacets.facets.get(TargetingSearchFacetsEnum.targetAudienceRegion) || [];
        const aocIds = aocFacets.map(facet => +facet.value);
        this.journodexRegionService.getByGeographyIds(aocIds).subscribe(areasOfCoverage => {
            areasOfCoverage.forEach(aoc => {
                switch (aoc.geographicType) {
                    case GeographyTypesEnum.country:
                        this.aocCountryFilter.options.push(
                            {
                                label: aoc.displayString.substring(0, aoc.displayString.lastIndexOf('(')),
                                value: aoc.collectionId,
                                count: aocFacets.find(x => +x.value === aoc.collectionId).count,
                            }
                        );
                        break;
                    case GeographyTypesEnum.province:
                        this.aocProvinceFilter.options.push(
                            {
                                label: aoc.displayString.substring(0, aoc.displayString.lastIndexOf('(')),
                                value: aoc.collectionId,
                                count: aocFacets.find(x => +x.value === aoc.collectionId).count,
                            }
                        );
                        break;
                    case GeographyTypesEnum.city:
                        this.aocCityFilter.options.push(
                            {
                                label: aoc.displayString.substring(0, aoc.displayString.lastIndexOf('(')),
                                value: aoc.collectionId,
                                count: aocFacets.find(x => +x.value === aoc.collectionId).count,
                            }
                        );
                        break;
                }
            });
            this.aocCountryFilter.setValue(this.aocCountryFilter.getValue());
            this.aocCountryFilter.loading = false;
            this.aocProvinceFilter.setValue(this.aocProvinceFilter.getValue());
            this.aocProvinceFilter.loading = false;
            this.aocCityFilter.setValue(this.aocCityFilter.getValue());
            this.aocCityFilter.loading = false;
        });
        const mediaMarketFacets = this.primaryFacets && this.primaryFacets.facets.get(TargetingSearchFacetsEnum.mediaMarket) || [];
        const mmIds = mediaMarketFacets.map(facet => +facet.value);
        this.journodexEnumsService.getMediaMarketsByIds(mmIds).subscribe(returnedMediaMarkets => {
            returnedMediaMarkets.forEach(mediaMarket => {
                this.mediaMarketFilter.options.push({
                    label: mediaMarket.displayString,
                    value: mediaMarket.id,
                    count: mediaMarketFacets.find(x => +x.value === mediaMarket.id).count,
                });
            });
            this.mediaMarketFilter.setValue(this.mediaMarketFilter.getValue());
            this.mediaMarketFilter.loading = false;
        });
    }

    protected getJobRoleOptions() {
        if (!this.jobRoles) {
            return;
        }

        const facets = this.primaryFacets && this.primaryFacets.facets.get(TargetingSearchFacetsEnum.jobRole) || [];

        this.jobRoleFilter.options = facets
            .filter(val => ('' + val.value).trim().length > 0)
            .map((val) => {
                const jobRole = this.jobRoles.find(role => role.id === +val.value);

                if (!jobRole) {
                    return null;
                }
                return {
                    label: jobRole.displayString,
                    value: jobRole.id,
                    count: val.count,
                };
            })
            .filter(val => val);
        const missingOptions = this.jobRoleFilter.getValue().filter(value => !this.jobRoleFilter.options.find(opt => opt.value === value));
        if (missingOptions.length > 0) {
            this.jobRoleFilter.options.push(
                ...missingOptions.map(val => {
                    const jobRole = this.jobRoles.find(role => role.id === +val);

                    if (!jobRole) {
                        return null;
                    }
                    return {
                        label: jobRole.displayString,
                        value: jobRole.id,
                    };
                }).filter(val => val)
            );
        }
        this.jobRoleFilter.setValue(this.jobRoleFilter.getValue()); // updates the chips
        this.jobRoleFilter.loading = false;
    }

    protected getSubjectOptions() {
        const facets = this.primaryFacets && this.primaryFacets.facets.get(TargetingSearchFacetsEnum.subject) || [];
        const subjectIds = new Set(facets.map(facet => +facet.value));
        const subjectsValue = this.subjectFilter.getValue();
        if (subjectsValue.length > 0) {
            subjectsValue.forEach(val => subjectIds.add(val));
        }

        combineLatest([
            this.subjectsService.getByIds(Array.from(subjectIds)),
            this.subjectsService.getChildrenByIds(Array.from(subjectIds))
        ]).subscribe(([parentSubjects, childSubjects]) => {
            const subjects = [...parentSubjects, ...childSubjects];
            this.subjectFilter.options = facets
                .filter(val => ('' + val.value).trim().length > 0)
                .map((val) => {
                    const subject = subjects.find(subj => +subj.id === +val.value);
                    if (!subject) {
                        return null;
                    }
                    return {
                        label: subject.name,
                        value: +subject.id,
                        count: val.count
                    };
                })
                .filter(val => val);
            const missingOptions = this.subjectFilter.getValue().filter(value => !this.subjectFilter.options.find(opt => opt.value === value));
            if (missingOptions.length > 0) {
                this.subjectFilter.options.push(
                    ...missingOptions.map(val => {
                        const subject = subjects.find(subj => +subj.id === +val);
                        if (!subject) {
                            return null;
                        }
                        return {
                            label: subject.name,
                            value: +subject.id
                        };
                    }).filter(val => val)
                );
            }
            this.subjectFilter.setValue(this.subjectFilter.getValue());
            this.subjectFilter.loading = false;
        });
    }
    //#endregion
}
